import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Users, FolderArchive, CalendarClock, UserPlus, DatabaseBackup, UserCog, FileSignature, ClipboardList } from "lucide-react";
import { api, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { PageHeader, Card, StatusBadge } from "@/components/Ui";

function Kpi({ icon: I, label, value, testId, to }) {
  const inner = (
    <Card className="p-5 hover:border-slate-300 transition-colors h-full">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        <I className="h-4 w-4 text-slate-400" />
      </div>
      <p className="font-heading text-3xl font-bold text-slate-900 mt-3" data-testid={testId}>{value ?? "—"}</p>
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

const WAIT_ST = { sent: "email inviata", delivered: "email aperta", created: "in coda" };

function PendingContracts({ items }) {
  if (!items?.length) return null;
  return (
    <Card className="mb-8" data-testid="dashboard-pending-contracts">
      <div className="p-5 border-b border-slate-100 flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-sky-700" />
        <h3 className="font-heading font-semibold text-slate-900">Contratti in attesa di firma</h3>
        <span className="ml-auto text-xs font-semibold text-slate-600" data-testid="pending-contracts-count">{items.length}</span>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((c) => (
          <Link key={c.id} to={`/prospects/${c.prospect_id}/contract`} className="px-5 py-3 grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_1fr_auto] gap-x-2 gap-y-1 items-center hover:bg-slate-50 transition-colors" data-testid={`pending-contract-${c.id}`}>
            <span className="text-sm font-medium text-slate-900">{c.client}</span>
            <span className="text-xs text-slate-600 col-span-2 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1">Deve firmare: <b>{c.waiting_for || "—"}</b>{c.waiting_status && WAIT_ST[c.waiting_status] ? ` (${WAIT_ST[c.waiting_status]})` : ""}</span>
            <span className={`text-xs font-semibold sm:col-start-3 row-start-1 col-start-2 ${c.days >= 7 ? "text-red-700" : "text-slate-600"}`} data-testid={`pending-contract-days-${c.id}`}>{c.days} {c.days === 1 ? "giorno" : "giorni"}</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}

function RolesToAssign({ items }) {
  if (!items?.length) return null;
  return (
    <Card className="mb-8 border-amber-200" data-testid="dashboard-roles-to-assign">
      <div className="p-5 border-b border-amber-100 bg-amber-50/60 flex items-center gap-2">
        <UserCog className="h-4 w-4 text-amber-700" />
        <h3 className="font-heading font-semibold text-slate-900">Trust in bozza con ruoli da assegnare</h3>
        <span className="ml-auto text-xs font-semibold text-amber-800" data-testid="roles-to-assign-count">{items.length}</span>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((t) => (
          <Link key={t.id} to={`/trusts/${t.id}`} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid={`roles-to-assign-${t.id}`}>
            <span className="text-sm font-medium text-slate-900">{t.name}</span>
            <span className="text-xs text-amber-800">{t.pending} {t.pending === 1 ? "soggetto" : "soggetti"} con ruolo “Da definire”</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}

function ActivitiesMonth({ s }) {
  if (!s) return null;
  const month = new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const hours = Number(s.hours || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
  return (
    <Card className="mb-8" data-testid="dashboard-activities-month">
      <div className="p-5 border-b border-slate-100 flex items-center gap-2">
        <ClipboardList className="h-4 w-4 text-sky-700" />
        <h3 className="font-heading font-semibold text-slate-900">Attività del mese <span className="text-slate-400 font-normal capitalize">· {month}</span></h3>
      </div>
      <div className="grid grid-cols-3 divide-x divide-slate-100">
        <div className="p-5"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Attività</p><p className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="activities-month-count">{s.count}</p></div>
        <div className="p-5"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ore totali</p><p className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="activities-month-hours">{hours} h</p></div>
        <div className="p-5"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trust coinvolti</p><p className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="activities-month-trusts">{s.trusts}</p></div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user, can } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <PageHeader eyebrow={ROLE_LABELS[user.role]} title={`Buongiorno, ${user.name?.split(" ")[0] || ""}`} subtitle="Panoramica dei Trust, delle scadenze e dello stato di sicurezza della piattaforma." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <Kpi icon={Briefcase} label="Trust" value={d?.trusts_total} testId="kpi-trusts" to="/trusts" />
        {can("subjects") && <Kpi icon={Users} label="Soggetti" value={d?.subjects_total} testId="kpi-subjects" to="/subjects" />}
        <Kpi icon={FolderArchive} label="Documenti" value={d?.documents_total} testId="kpi-documents" to="/documents" />
        <Kpi icon={CalendarClock} label="Scadenze scadute" value={d?.deadlines_overdue} testId="kpi-overdue" to="/deadlines" />
        {can("prospects") && <Kpi icon={UserPlus} label="Nuovi clienti" value={d?.prospects_total} testId="kpi-prospects" to="/prospects" />}
        {user.role === "admin" && <Kpi icon={DatabaseBackup} label="Ultimo backup" value={d?.last_backup_at ? fmtDate(d.last_backup_at) : "Nessuno"} testId="kpi-last-backup" to="/backup" />}
      </div>
      <PendingContracts items={d?.pending_contracts} />
      <ActivitiesMonth s={d?.activities_month} />
      <RolesToAssign items={d?.roles_to_assign} />
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <div className="p-5 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-900">Scadenze nei prossimi 30 giorni</h3></div>
          <div className="divide-y divide-slate-100" data-testid="dashboard-deadlines">
            {(d?.deadlines_upcoming || []).length === 0 && <p className="p-8 text-center text-sm text-slate-400">Nessuna scadenza imminente</p>}
            {(d?.deadlines_upcoming || []).map((x) => (
              <div key={x.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div><p className="text-sm font-medium text-slate-900">{x.title}</p><p className="text-xs text-slate-500">{x.category || "—"}</p></div>
                <div className="flex items-center gap-3"><span className="font-mono text-xs text-slate-600">{fmtDate(x.due_date)}</span><StatusBadge value={x.priority} /></div>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <div className="p-5 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-900">Trust per stato</h3></div>
          <div className="p-5 space-y-3">
            {Object.entries(d?.trusts_by_status || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between"><StatusBadge value={k} /><span className="font-mono text-sm">{v}</span></div>
            ))}
            {d && Object.keys(d.trusts_by_status).length === 0 && <p className="text-sm text-slate-400">Nessun Trust</p>}
          </div>
          {d?.recent_audit && (
            <>
              <div className="p-5 border-y border-slate-100"><h3 className="font-heading font-semibold text-slate-900">Ultimi eventi di audit</h3></div>
              <div className="p-5 space-y-2.5">
                {d.recent_audit.map((a, i) => (
                  <div key={i} className="text-xs"><span className="font-mono text-slate-800">{a.action}</span><span className="text-slate-400"> · {a.user_email || "sistema"} · {fmtDateTime(a.created_at)}</span></div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
